"use client";

import { c as _c } from "react/compiler-runtime";
import { type InfiniteData, type UseInfiniteQueryResult, useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { isLoadingBoundaries } from "../db/get-index";
import { hybridQueryOptions } from "../lib/react-query/query-options";
import type { FilterResultPage, FilterSearchParams } from "../types/filter";
import { normalizeFilterSearchParams } from "../utils/filterSearch/utils";
import { useGeoSearch } from "./use-geosearch";
import { useLocale } from "./use-locale";
import { useSearchState } from "./use-search-state";
import { useSearchQueryContext } from "./use-skill-vector-context";
export interface HybridSearchOptions {
  rerankInstruction: string;
  searchQuestion: string;
}
export function useHybridSearch(options) {
  const $ = _c(29);
  const locale = useLocale();
  const [isMounted, setIsMounted] = useState(false);
  const {
    geoLocation,
    boundaries,
    radius
  } = useGeoSearch();
  const {
    filterQuery,
    getQueryState,
    searchQuery
  } = useSearchState();
  const {
    sessionBound
  } = useSearchQueryContext();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setIsMounted(true);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== filterQuery || $[3] !== geoLocation || $[4] !== searchQuery) {
    t2 = Object.values(filterQuery).some(_temp) || Boolean(geoLocation) || Boolean(searchQuery);
    $[2] = filterQuery;
    $[3] = geoLocation;
    $[4] = searchQuery;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const hasFilters = t2;
  let t3;
  if ($[6] !== boundaries || $[7] !== geoLocation || $[8] !== radius) {
    t3 = isLoadingBoundaries(geoLocation, radius, boundaries);
    $[6] = boundaries;
    $[7] = geoLocation;
    $[8] = radius;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const isLoading = t3;
  const isValidQuery = Boolean(options?.searchQuestion);
  let t4;
  let t5;
  if ($[10] !== boundaries || $[11] !== geoLocation || $[12] !== getQueryState || $[13] !== isLoading || $[14] !== isMounted || $[15] !== isValidQuery || $[16] !== locale || $[17] !== options?.rerankInstruction || $[18] !== options?.searchQuestion || $[19] !== radius || $[20] !== searchQuery || $[21] !== sessionBound) {
    const filter = getQueryState();
    const filterSearchParams = normalizeFilterSearchParams({
      searchQuery: searchQuery || "",
      filter,
      geoLocation,
      boundaries,
      radius
    });
    t4 = useInfiniteQuery;
    t5 = hybridQueryOptions.questionSearch(locale, options?.searchQuestion ?? "", options?.rerankInstruction ?? "", filterSearchParams, {
      enabled: isMounted && !isLoading && isValidQuery,
      sessionBound
    });
    $[10] = boundaries;
    $[11] = geoLocation;
    $[12] = getQueryState;
    $[13] = isLoading;
    $[14] = isMounted;
    $[15] = isValidQuery;
    $[16] = locale;
    $[17] = options?.rerankInstruction;
    $[18] = options?.searchQuestion;
    $[19] = radius;
    $[20] = searchQuery;
    $[21] = sessionBound;
    $[22] = t4;
    $[23] = t5;
  } else {
    t4 = $[22];
    t5 = $[23];
  }
  const results = t4(t5);
  let finalResults = results;
  if (isLoading && isValidQuery) {
    let t6;
    if ($[24] !== results) {
      t6 = {
        ...results,
        isFetching: true,
        isLoading: true,
        isFetchingNextPage: false,
        isFetched: false
      };
      $[24] = results;
      $[25] = t6;
    } else {
      t6 = $[25];
    }
    finalResults = t6 as UseInfiniteQueryResult<InfiniteData<FilterResultPage, unknown>, Error>;
  }
  let t6;
  if ($[26] !== finalResults || $[27] !== hasFilters) {
    t6 = {
      ...finalResults,
      hasFilters
    };
    $[26] = finalResults;
    $[27] = hasFilters;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  return t6;
}
function _temp(v) {
  return v && v.length > 0;
}